// Copy for the paywall flow frames. Rough — it sets tone, not final wording.
//
// Tracks the spec of 12 Aug. Two things in it moved the flow: the CAM callback
// is gone (the care request goes out, providers answer on the page), and the
// paywall now collects what the callback used to — so it is a question set as
// well as a gate.

export const FLOW = {
  // No content for F0, F1 or F2: they render the real care circle — care search,
  // then the Shortlists tab at two moments — which brings its own fixtures. A
  // copy of those screens here would be a second version of each, free to drift
  // from the one that ships.
  //
  // The shortlist as its own page, kept for the hidden rail-vs-page section and
  // the row drawer. Same columns the table had when it was a panel — everything
  // that doesn't earn a column lives in the row drawer.
  send: {
    back: 'Margarette Qualley’s care circle',
    title: 'Shortlist 1 · 1/06/2026',
    subtitle: 'Margaret H. · DOM care · Chelmsford',
    publish: 'Publish',
    columns: ['Provider', 'Tier', 'Sent', 'Status'],
    rows: [
      { name: 'Bluebird Care', tier: 'Premium', sent: '3 Aug', status: 'Paid' },
      {
        name: 'Home Instead',
        tier: 'Premium',
        sent: '3 Aug',
        status: 'Pending',
      },
      {
        name: 'Radfield Home',
        tier: 'Premium',
        sent: '—',
        status: 'Not sent',
      },
      { name: 'Caremark', tier: 'Standard', sent: '3 Aug', status: '—' },
    ],
    cta: 'Send paywall link',
    note: 'Nothing is handed over until Stripe confirms.',
  },
  // What the table can't hold, on a row click. The note is the test case: it
  // is the thing that broke the narrow rail.
  rowDrawer: {
    provider: 'Home Instead',
    subtitle: 'Premium · Available · Chelmsford',
    detail: [
      { label: 'Contact', value: 'referrals@homeinstead.co.uk' },
      { label: 'Distance', value: '2.4 miles' },
      { label: 'Care types', value: 'Live-in, domiciliary, dementia' },
    ],
    stateLabel: 'Paywall',
    state: [
      { label: 'Link sent', value: '4 Aug · 09:12' },
      { label: 'Paid', value: 'Not yet' },
      { label: 'On the carer’s shortlist', value: 'No' },
    ],
    noteLabel: 'Your note',
    note: 'Spoke to the branch manager. They have capacity from the 18th and are keen, but want the funding picture before they commit.',
    cta: 'Resend paywall link',
  },
  // Plain-text email. No brand furniture — it has to survive any client, and
  // everything it says is outside the paywall. This is the care request going
  // out, so it is also the thing that replaced the phone call.
  // Goes to every dom care provider on the list, premium or not. The footer used
  // to say they were getting it because they were premium; nobody is getting it
  // for that reason now.
  email: {
    fromName: 'KareHero referrals',
    fromAddress: 'referrals@karehero.com',
    to: 'referrals@radfieldhome.co.uk',
    subject: 'A new DOM care lead near you',
    received: 'Thu 7 Aug, 09:14',
    greeting: 'Hello Radfield Home,',
    body: [
      'We have a client in Chelmsford who matches your service. They are self-funded, need live in care seven days a week, and have dementia and mobility needs.',
      'Tell us whether you can take them. It takes a minute and there is nothing to pay to answer.',
      'Five providers can unlock this lead. Three places are left.',
    ],
    cta: 'View the lead',
    signOff: ['Abbie Fenton', 'KareHero'],
    foot: 'You are receiving this because Radfield Home covers DOM care in this area.',
    unsubscribe: 'Unsubscribe',
  },
  paywall: {
    // The heading names the thing: a provider arriving cold from their inbox has
    // to know in one line what this page is and whether it is near them. It used
    // to lead on the redaction ("Anonymised lead"), which is the provider's
    // objection rather than the offer, and then on the provenance alone, which
    // reads as an attribution rather than a heading.
    title: 'New care lead · Chelmsford',
    // The left card's own heading. The page header names the lead; the card says
    // what is in it, so a provider scanning knows the fields below are the
    // client's requirements rather than their own account details.
    cardTitle: 'Lead details',
    // What it is, in one line, without repeating the unlock mechanic — the
    // hidden block already says "Unlocks on payment", and the town is in the
    // page header above it.
    cardSubtitle: 'We have someone nearby who needs the care you provide.',
    // Provenance, demoted under it. Care Sourcer is the reason to trust the lead,
    // not the name of it.
    kicker: 'Powered by Care Sourcer',
    visible: [
      { label: 'Funding', value: 'Self-funded' },
      { label: 'Care needs', value: 'Live-in, 7 days a week' },
      { label: 'Complexity', value: 'Dementia, mobility support' },
      { label: 'Starts', value: 'Within 2 weeks' },
    ],
    hiddenLabel: 'Unlocks on payment',
    hidden: [
      { label: 'Name', value: 'Margaret Halloway' },
      { label: 'Email', value: 'm.halloway@example.com' },
      { label: 'Case manager', value: 'Abbie Fenton · direct line' },
    ],
    // £10, decided 18 Aug. Shown ex VAT here and totalled with it at checkout,
    // which is the same order the old £40 was in. Whether the price is shown ex or
    // inc VAT is still open, so the split is the assumption to check rather than
    // the price itself.
    price: '£10',
    cta: 'Unlock this lead',
  },
  // The callback, as a page. Everything CAM used to extract on the phone, asked
  // at the one moment a provider is definitely engaged.
  //
  // No "will you accept this person" question. Paying to unlock is the answer to
  // it, so asking is asking twice.
  answers: {
    title: 'Can you take this one?',
    // The card's own heading, opposite 'Lead details' on the screen before: that
    // card is the client's, this one is theirs. The page header asks the
    // question, the card names what answering it takes.
    cardTitle: 'Your details',
    // One line, and it asks rather than explains. The page used to open on
    // "Prefilled from the care request", which described our plumbing at the
    // moment the provider wants to know what is being asked of them.
    intro: 'We need a few details from you for this request.',
    // Contact details, and the only inputs on the page. Stripe hands us a billing
    // email, which is not a person who answers the phone. No "First time only"
    // badge and no "we ask for these once" line: two pieces of reassurance about a
    // form of two fields cost more attention than the form does.
    //
    // No terms checkbox any more. Accepting them is folded into the act of paying,
    // said above the button in `checkout.accept`, because a tick that gates a
    // payment button is a second yes for the same decision.
    firstTime: {
      fields: [
        { label: 'Named contact', placeholder: 'Who should we send leads to?' },
        { label: 'Direct number', placeholder: 'A line that gets answered' },
      ],
    },
    // The rate, and the one thing on this page only the provider can put right.
    // We hold a rate from CareSourcer and a family reads it off their shortlist,
    // so a provider who knows it has moved has nowhere to say so today.
    //
    // On every lead, unlike the contact block above. A named contact is close to
    // a fixed fact, which is what makes asking once reasonable; a rate is the
    // thing most likely to have changed since the last lead, so gating it behind
    // the first one would make it uncorrectable exactly when it starts to be
    // wrong. Prefilled with what we hold, so the common case is reading it and
    // moving on.
    //
    // The period is theirs to pick rather than ours to assume. Dom care is
    // quoted hourly by most, daily or weekly by some, and a live-in package by
    // the month, so a fixed unit would make somebody restate their price in a
    // shape they do not use — and `price` in care search is one string carrying
    // whichever shape it happened to arrive in.
    rate: {
      // Currency in the label rather than as a start adornment in the field.
      // llama's filled input pushes the value down to clear its floating label
      // (`padding: '31px 12px 8px'`) but pins the adornment's margin-top to 0,
      // so a £ inside the field renders above the number it belongs to. That is
      // a llama bug rather than this field's, and worth fixing there.
      label: 'Your rate (£)',
      defaultValue: '28.50',
      periodLabel: 'Period',
      periods: ['Hourly', 'Daily', 'Weekly', 'Monthly'],
      defaultPeriod: 'Hourly',
      // Why it is worth a field: the number is not for us, it is what the family
      // is shown.
      hint: 'This is the rate families see on their shortlist.',
    },
    // Nothing is collected about the request. Earliest start date, minimum package
    // and complex needs all came off: every one of them was us asking a provider
    // to retype what the lead already says, which turned the page into a form at
    // the moment it should be a decision. The confirm box went the same way —
    // paying is the yes, so a tick above the pay button was asking the same
    // question twice.
    //
    // What is left is the request as it came through on the screen before,
    // read-only. The lines are not restated here: they are read from
    // `paywall.visible`, so what a provider pays against is by construction what
    // they were shown.
    confirmLabel: 'What we have for this request',
    cta: 'Unlock this lead',
    // The no, under the price on both screens that show the lead. One label, used
    // twice, because it is one act: a provider who reads the profile and finds they
    // cannot help should not have to hunt for it, and neither should one who only
    // works it out while answering the questions.
    decline: 'Can’t take this one',
  },
  checkout: {
    title: 'Pay to unlock',
    lines: [
      { label: 'DOM care lead · Chelmsford', value: '£10.00' },
      { label: 'VAT', value: '£2.00' },
    ],
    total: { label: 'Total', value: '£12.00' },
    // Above the button, because that is where the yes is. The terms used to be a
    // tick in the form, which is a second yes for the same decision: a provider
    // who has decided to pay has to stop and agree to pay again. Said as a fact
    // about the button instead, so it is read at the moment it applies.
    //
    // Split so the link sits inside the sentence — the words a person clicks
    // should be the words naming what they open.
    accept: {
      before: 'By paying you accept the KareHero ',
      link: 'provider terms',
      after: '.',
    },
    cta: 'Pay £12.00',
    terms: 'You are paying for a lead, not a placement.',
    refunds:
      'If the client places with someone else, there is no refund. You paid for the lead and the lead was delivered.',
  },
  unlocked: {
    title: 'Lead unlocked',
    sub: 'Margaret Halloway · Chelmsford',
    // The whole lead, on one screen. The details that were open from the start
    // are read back here beside the ones payment released: a provider acting on
    // this page is about to ring a stranger about a care package, and sending
    // them back to the email for the funding and the care needs is asking them
    // to hold half a lead in their head.
    //
    // Not restated as copy. Read from `paywall.visible`, so what a provider
    // keeps is by construction the profile they paid on.
    requestLabel: 'The request',
    // The other half named, because the screen now shows two groups and the
    // difference between them is what the money bought.
    unlockedLabel: 'What you unlocked',
    items: [
      { label: 'Client email', value: 'm.halloway@example.com' },
      { label: 'Case manager', value: 'Abbie Fenton' },
      { label: 'Case manager email', value: 'abbie.fenton@karehero.com' },
      { label: 'Direct line', value: 'Opens a channel in KareHero' },
    ],
    cta: 'Message the case manager',
    // Dull, and a reason not to pay if it is missing.
    invoice: 'Download invoice',
    // Not "you now appear on the client's shortlist". Publishing is a separate
    // CAM action that happens later, so a paid provider is not on the shortlist
    // yet and the paywall cannot promise they will be. What has actually
    // happened at this moment is the handover of their details and a person
    // picking it up.
    note: 'Your details have been shared with the client and a member of our team will reach out to you.',
  },
  // The member's push. They are not told anything about payment — from their
  // side a provider simply became available.
  push: {
    app: 'KareHero',
    when: 'now',
    title: 'A new provider on your shortlist',
    body: 'Riverside lodge has confirmed they can help. Have a look when you’re ready.',
    time: '9:41',
    date: 'Thursday 7 August',
  },
  // No camShortlist content: F9 renders the real Shortlists tab, published. The
  // four-state model the feature spec asks for is argued in that frame's brief
  // rather than drawn as a second table here — a sketch of a screen that exists
  // is just a version of it free to drift.
  // No override content: the escape hatch is the publish confirm now, and F12
  // renders the real dialog. A sketch of it here would be a second version of a
  // decision that already exists in the tab.
  // The page in every state that is not the open one. Each is a full screen
  // with its own copy — never a banner on the open page, and never a generic
  // error. First draft: Steph owns the commercial wording.
  states: {
    // The one-tap no, answered. A provider who cannot take this person is worth
    // more to us saying so than going quiet, so the tap is free, it is on the
    // email as well as the page, and this screen is what it lands on.
    //
    // No reason picker: we are not asking why. That keeps the no to a single tap,
    // which is the only reason anyone would bother, and it means this screen has
    // nothing to submit — it is the end of the interaction, not a form.
    declined: {
      id: 'S4',
      title: 'Thanks for telling us',
      body: [
        'We’ve taken you off this one, so we won’t chase you about it again.',
        'We’ll keep sending you leads that fit. Nothing has been charged, and nothing was unlocked.',
      ],
    },
    failed: {
      id: 'S2',
      // Deliberately not "your card was declined". One screen covers a declined
      // card, a dropped session and anything else that breaks between the button
      // and Stripe confirming, and naming one of them is a guess — a provider
      // told their card failed when the session timed out goes and rings their
      // bank. The two lines that matter are the same either way: no money taken,
      // nothing lost.
      title: 'Something went wrong',
      body: [
        'We haven’t taken any payment. Your place is still held and your answers are still here.',
      ],
      cta: 'Try again',
    },
  },
  // The carer with nobody on their list yet. The spec calls this the unresolved
  // one, and it is the end of the same thread as a shortlist nobody pays for.
  emptyShortlist: {
    title: 'We’re still finding providers',
    body: 'We’ve sent your details to five providers near you. As soon as one can help, they’ll show up here.',
    reassure: 'We’ll let you know the moment that happens.',
    cta: 'Talk to Abbie',
    note: 'Nothing here is decided. The screen is a placeholder for a decision nobody has made: what a carer is told when the paywall produces nobody.',
  },
  // Provider facing, like every other screen they reach from the email. It used
  // to be written for a CAM — "Home Instead did not pay", with a Publish
  // shortlist button — which is the wrong person entirely: the provider opening
  // a stale link is the one reading this, and all they need is why it will not
  // open.
  // One screen for every way a lead can be over by the time a provider gets to
  // it: the link aged out, or the client arranged their care and the lead closed.
  // They were drafted as two screens on the argument that a wasted trip has to
  // read differently from an old link. It does not: from the provider's side both
  // are the same fact, there is nothing for them to do about either, and a second
  // screen would only be a second way of saying so.
  //
  // The charge line is the load-bearing part. Without it a provider who paid
  // moments ago reads this as having lost the money, which is a phone call to
  // Steph rather than a shrug.
  timeout: {
    title: 'This lead has closed',
    note: 'Nothing has been charged.',
  },
};
